import React, { useRef, useEffect, useState, useMemo } from 'react';

const Sparkline = ({ data, isUp, row_trend }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data || data.length < 2) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;

    ctx.beginPath();
    data.forEach((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - ((v - min) / range) * (h - 4) - 2;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.strokeStyle = isUp ? '#10b981' : row_trend === 'neutral' ? '#eab308' : '#f43f5e';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Fill under the line
    ctx.lineTo(w, h);
    ctx.lineTo(0, h);
    ctx.closePath();
    ctx.fillStyle = isUp ? 'rgba(16, 185, 129, 0.15)' : row_trend === 'neutral' ? 'rgba(234, 179, 8, 0.1)' : 'rgba(244, 63, 94, 0.15)';
    ctx.fill();
  }, [data, isUp]);

  return <canvas ref={canvasRef} width={120} height={30} className="mx-auto" />;
};

const fmtPct = (v) => {
  if (v == null) return <span className="text-zinc-700">—</span>;
  const cls = v >= 0 ? 'text-emerald-400 font-black' : 'text-rose-400 font-black';
  return <span className={cls}>{v >= 0 ? '+' : ''}{v.toFixed(2)}%</span>;
};const RankingsTable = ({ 
  data, 
  onRowClick, 
  nameLabel = "Index", 
  valueLabel = "Current RSC", 
  valueKey = "current_rsc",
  showAsPercent = false
}) => {
  const [sortConfig, setSortConfig] = useState({ key: valueKey, direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');

  const filteredData = useMemo(() => {
    if (!data) return [];
    if (!searchTerm) return data;
    const lowerSearch = searchTerm.toLowerCase();
    return data.filter(item => 
      item.name.toLowerCase().includes(lowerSearch)
    );
  }, [data, searchTerm]);

  const sortedData = useMemo(() => {
    const sortableData = [...filteredData];
    if (sortConfig.key !== null) {
      sortableData.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [filteredData, sortConfig]);

  const requestSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <span className="text-zinc-700 ml-1">⇅</span>;
    return sortConfig.direction === 'asc' ? <span className="text-emerald-500 ml-1">▲</span> : <span className="text-emerald-500 ml-1">▼</span>;
  };

  if (!data || data.length === 0) return (
    <div className="flex h-40 items-center justify-center text-zinc-500 font-black uppercase text-[12px] tracking-widest bg-zinc-900/20 rounded-xl border border-dashed border-zinc-800">
      No ranking data found
    </div>
  );

  return (
    <div className="w-full space-y-4">
      <div className="flex justify-start items-center px-1">
        <div className="relative w-full max-w-sm group">
          <input
            type="text"
            placeholder={`Search ${nameLabel.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-zinc-900/50 border border-zinc-700/50 text-white text-[14px] px-10 py-2 rounded-lg 
                       focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/10
                       transition-all placeholder:text-zinc-600 font-mono"
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center">
            <svg 
              className="w-4 h-4 text-white transition-colors group-focus-within:text-emerald-500" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              strokeWidth="3"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
            >
              ✕
            </button>
          )}
        </div>
      </div>
      
      <div className="w-full overflow-x-auto shadow-2xl rounded-xl border border-zinc-800/50">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-zinc-900 border-b-2 border-zinc-800">
            <th className="px-4 py-3 text-left text-zinc-100 font-black uppercase tracking-wider w-10">#</th>
            <th 
              className="px-4 py-3 text-left text-white font-black uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-zinc-800 transition-colors"
              onClick={() => requestSort('name')}
            >
              {nameLabel} {getSortIcon('name')}
            </th>
            <th className="px-4 py-3 text-center text-white font-black uppercase tracking-wider">Trend</th>
            <th className="px-4 py-3 text-center text-white font-black uppercase tracking-wider min-w-[140px]">Chart</th>
            <th 
              className="px-4 py-3 text-right text-white font-black uppercase tracking-wider cursor-pointer hover:bg-zinc-800 transition-colors"
              onClick={() => requestSort(valueKey)}
            >
              {valueLabel} {getSortIcon(valueKey)}
            </th>
            {['1W', '1M', '3M', '6M', '1Y'].map(period => (
              <th 
                key={period}
                className="px-4 py-3 text-right text-white font-black uppercase tracking-wider cursor-pointer hover:bg-zinc-800 transition-colors"
                onClick={() => requestSort(period)}
              >
                {period}% {getSortIcon(period)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, i) => {
            const isUp = row.trend === 'up' || (row.change_pct !== undefined ? row.change_pct >= 0 : row.current_rsc >= 1.0);
            const displayVal = row[valueKey];
            return (
              <tr
                key={row.name}
                className="border-b border-zinc-800/80 hover:bg-zinc-800/50 transition-colors cursor-pointer group"
                onClick={() => onRowClick && onRowClick(row.name)}
              >
                <td className="px-4 py-3 text-zinc-400 font-mono text-[13px] group-hover:text-emerald-500 transition-colors">
                  {i + 1}
                </td>
                <td className="px-4 py-3">
                  <span className="text-blue-400 hover:text-blue-300 font-black text-[14px] transition-colors uppercase tracking-tight">
                    {row.name}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  {row.trend === 'neutral' 
                    ? <span className="text-zinc-500 font-black">-</span>
                    : isUp
                      ? <span className="text-emerald-500 text-xl drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]">▲</span>
                      : <span className="text-rose-500 text-xl drop-shadow-[0_0_8px_rgba(244,63,94,0.5)]">▼</span>
                  }
                </td>
                <td className="px-4 py-3">
                  <Sparkline data={row.sparkline} isUp={isUp} row_trend={row.trend} />
                </td>
                <td className="px-4 py-3 text-right font-mono font-black text-[14px] text-white">
                  {displayVal !== null && displayVal !== undefined ? (
                    showAsPercent ? `${displayVal > 0 ? '+' : ''}${displayVal.toFixed(2)}%` : displayVal.toFixed(4)
                  ) : '—'}
                </td>
                <td className="px-4 py-3 text-right font-mono text-[13px]">{fmtPct(row['1W'])}</td>
                <td className="px-4 py-3 text-right font-mono text-[13px]">{fmtPct(row['1M'])}</td>
                <td className="px-4 py-3 text-right font-mono text-[13px]">{fmtPct(row['3M'])}</td>
                <td className="px-4 py-3 text-right font-mono text-[13px]">{fmtPct(row['6M'])}</td>
                <td className="px-4 py-3 text-right font-mono text-[13px]">{fmtPct(row['1Y'])}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default RankingsTable;
